import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import HomeSection from '../HomeSection';
import EditorialPostCard from '../../../molecules/EditorialPostCard';
import EditorialCardRail from '../../../molecules/EditorialCardRail';
import EditorialEmptyState from '../../../molecules/EditorialEmptyState';
import TimelineEntityCard from '../../TimelineEntityCard';
import SuggestedUserCard from '../../SuggestedUserCard';
import CardActionsMenu from '../../../molecules/CardActionsMenu';
import ContentWithLinkPreviews from '../../../molecules/ContentWithLinkPreviews';
import PostLinkPreview from '../../../molecules/PostLinkPreview';
import { extractPrimaryLinkFromText } from '../../../../utils/linkDetection';
import { isSupportedLinkPreviewUrl } from '../../../molecules/LinkWithPreview/supportedLinkPreview.ts';
import PostDetailModal from '../../PostDetailModal';
import ScheduledEntityCommentsModal from '../../../molecules/ScheduledEntityCommentsModal';
import EventCreateModal from '../../../molecules/EventCreateModal';
import UserCardAvatar from '../../../molecules/UserCardAvatar';
import StartPostButton from '../../../atoms/StartPostButton';
import { NewPostModal } from '../../HomePage/NewPostModal';
import ConfirmDialog from '../../../molecules/ConfirmDialog';
import { usePostDeleteDialog } from '../../../../hooks/usePostDeleteDialog';
import { useDropdown } from '../../../../hooks/useDropdown';
import { useClickableLinks } from '../../../../hooks/useClickableLinks';
import useTimelineEngagement from '../../../../hooks/useTimelineEngagement';
import useLocaleDateFormatter from '../../../../hooks/useLocaleDateFormatter';
import { useTranslation } from 'react-i18next';
import createDeletePostMessageBuilder from '../../../../utils/deletePostMessage';
import { normalizePostBody } from '../../../../utils/postBody';
import { findFirstImageAttachmentUrl } from '../../../../utils/imageUtils';
import { toPublicBandImageUrl, toPublicProfilePictureUrl } from '../../../../utils/mediaUrl';
import { formatLastSeenRelative } from '../../../../utils/presenceUtils';
import { isPostTimelineItem } from '../../../../utils/timelineItems';
import { resolvePostCardIdentity } from '../../../../utils/postCardIdentity';
import { TIMELINE_ITEM_KINDS } from '../../../../services/api/timeline';
import classNames from '../../../../utils/classNames';
import bandIcon from '../../../../images/band-icon.svg';
import './index.scss';

const SpotlightIcon = () => (
  <svg
    className="editorial-post-header__badge-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 2l2.6 6.6L22 10l-5.5 4.7L18.2 22 12 18l-6.2 4 1.7-7.3L2 10l7.4-1.4L12 2z" />
  </svg>
);

const canViewerManagePost = (post) => post?.canManage === true;

const isPinnedPost = (post) => (
  post?.pinned === true
  || post?.isPinned === true
  || Boolean(post?.pinnedAt)
);

export default function HomePostsSection({
  id = 'posts',
  eyebrow,
  title,
  side,
  showSide = true,
  posts = [],
  currentUserId,
  readOnly = false,
  // composer
  allowComposer = true,
  isComposerOpen,
  composerMode,
  openCreateComposer,
  startEditingPost,
  closeComposer,
  editingPost,
  postContent,
  setPostContent,
  postVisibility,
  setPostVisibility,
  editContent,
  setEditContent,
  editVisibility,
  setEditVisibility,
  postAttachments,
  editAttachments,
  addCreateAttachments,
  removeCreateAttachment,
  addEditAttachments,
  removeEditAttachment,
  postAttachmentError,
  editAttachmentError,
  isSubmitting,
  maxAttachments,
  postVisibilityOptions,
  createPost,
  handleUpdatePost,
  deletePost,
  onPostMetaChange,
  onDeleteTimelineItem,
  hasMorePosts = false,
  isLoadingPosts = false,
  isLoadingMorePosts = false,
  loadMorePosts,
  showComposerToolbar = true,
  renderComposeButton,
  editLabel = 'Edit',
  deleteLabel = 'Delete',
  // empty state
  emptyTitle,
  emptyDescription,
  loadingTitle = 'Loading posts...',
  loadingDescription,
  postsError,
  postsErrorTitle = 'Unable to load posts',
  postsErrorDescription = 'Posts could not be loaded right now.',
  retryPostsLabel = 'Try again',
  onRetryPosts,
  composeLabel = 'Start a post',
  loadMoreLabel = 'Load more posts',
  loadingMoreLabel = 'Loading posts...',
  reactionsLabel = 'reactions',
  commentsLabel = 'comments',
  publicMedia = false,
}) {
  const { t } = useTranslation();
  const { formatDateTime, formatDate, formatTime } = useLocaleDateFormatter();
  const { dropdownOpenId, toggleDropdown, setDropdownOpenId } = useDropdown();
  const [editingScheduledEntity, setEditingScheduledEntity] = useState(null);
  const {
    openPostId,
    setOpenPostId,
    openScheduledEntityCommentsId,
    setOpenScheduledEntityCommentsId,
    renderSocialFooter,
  } = useTimelineEngagement({
    currentUserId: readOnly ? null : currentUserId,
    onItemMetaChange: readOnly ? null : onPostMetaChange,
    readOnly,
  });
  const renderPostBodyWithLinks = useClickableLinks();

  const timelineItems = useMemo(
    () => (Array.isArray(posts) ? posts : []),
    [posts]
  );
  const postItems = useMemo(
    () => timelineItems.filter(isPostTimelineItem),
    [timelineItems]
  );

  const deleteMessageBuilder = useMemo(() => createDeletePostMessageBuilder(t), [t]);
  const {
    state: deleteDialogState,
    requestDelete: handleRequestDeletePost,
    cancelDelete: handleCancelDeletePost,
    confirmDelete: handleConfirmDeletePost,
    message: deleteDialogMessage,
  } = usePostDeleteDialog(postItems, deletePost, { buildMessage: deleteMessageBuilder });

  const isEditing = composerMode === 'edit' && Boolean(editingPost);
  const composerContent = isEditing ? editContent : postContent;
  const composerSetContent = isEditing ? setEditContent : setPostContent;
  const composerVisibility = isEditing ? editVisibility : postVisibility;
  const composerSetVisibility = isEditing ? setEditVisibility : setPostVisibility;
  const composerAttachments = isEditing ? editAttachments : postAttachments;
  const handleAddAttachments = isEditing ? addEditAttachments : addCreateAttachments;
  const handleRemoveAttachment = isEditing ? removeEditAttachment : removeCreateAttachment;
  const attachmentError = isEditing ? editAttachmentError : postAttachmentError;
  const handleComposerSubmit = isEditing
    ? () => handleUpdatePost(editingPost.id)
    : createPost;

  const canCompose = !readOnly && allowComposer && typeof openCreateComposer === 'function';
  const currentActionUserId = readOnly ? null : currentUserId;
  const composeAction = canCompose
    ? (typeof renderComposeButton === 'function'
      ? renderComposeButton({ label: composeLabel, onClick: openCreateComposer })
      : <StartPostButton label={composeLabel} onClick={openCreateComposer} />)
    : null;
  const hasPostsLoadError = Boolean(postsError);
  const retryAction = hasPostsLoadError && typeof onRetryPosts === 'function' ? (
    <button
      type="button"
      className="home-posts__retry-btn"
      onClick={onRetryPosts}
      disabled={isLoadingPosts}
    >
      {retryPostsLabel}
    </button>
  ) : null;
  const emptyStateTitle = hasPostsLoadError
    ? postsErrorTitle
    : (isLoadingPosts ? loadingTitle : emptyTitle);
  const emptyStateDescription = hasPostsLoadError
    ? (postsError || postsErrorDescription)
    : (isLoadingPosts ? loadingDescription : emptyDescription);
  const emptyStateAction = hasPostsLoadError ? retryAction : (isLoadingPosts ? null : composeAction);

  const buildSocialFooter = useCallback((item) => renderSocialFooter(item, {
    commentTriggerClassName: 'home-posts__comment-trigger',
    visibilityWrapperClassName: 'home-posts__foot-visibility',
  }), [renderSocialFooter]);

  const handleStartEditingScheduledEntity = useCallback((item) => {
    if (item?.objectType !== 'EVENT') return;
    setEditingScheduledEntity(item);
  }, []);

  return (
    <HomeSection
      id={id}
      eyebrow={eyebrow}
      title={title}
      side={side}
      showSide={showSide}
      variant="elev"
      className="home-posts"
    >
      {canCompose && showComposerToolbar ? (
        <div className="home-posts__toolbar">
          {composeAction}
        </div>
      ) : null}

      {!readOnly && allowComposer && isComposerOpen ? (
        <NewPostModal
          mode={isEditing ? 'edit' : 'create'}
          postContent={composerContent}
          setPostContent={composerSetContent}
          postVisibility={composerVisibility}
          setPostVisibility={composerSetVisibility}
          attachments={composerAttachments}
          onAddAttachments={handleAddAttachments}
          onRemoveAttachment={handleRemoveAttachment}
          attachmentError={attachmentError}
          maxAttachments={maxAttachments}
          visibilityOptions={postVisibilityOptions}
          isSubmitting={isSubmitting}
          onSubmit={handleComposerSubmit}
          onClose={closeComposer}
        />
      ) : null}

      {timelineItems.length > 0 ? (
        <EditorialCardRail
          rows={1}
          className="home-posts__rail"
          trackClassName="home-posts__grid"
          previousLabel="Scroll posts left"
          nextLabel="Scroll posts right"
        >
          {timelineItems.map((post, idx) => {
            if (post?.timelineItemKind === TIMELINE_ITEM_KINDS.SUGGESTED_USER) {
              return (
                <div
                  key={post.id}
                  className="home-posts__timeline-item home-posts__timeline-item--suggested-user"
                >
                  <SuggestedUserCard
                    item={post}
                    currentUserId={currentActionUserId}
                    readOnly={readOnly}
                  />
                </div>
              );
            }

            if (!isPostTimelineItem(post)) {
              return (
                <div
                  key={post.id}
                  className="home-posts__timeline-item home-posts__timeline-item--entity"
                >
                  <TimelineEntityCard
                    item={post}
                    currentUserId={currentActionUserId}
                    dropdownOpenId={dropdownOpenId}
                    onToggleDropdown={readOnly ? null : toggleDropdown}
                    onDeleteEntity={readOnly ? null : onDeleteTimelineItem}
                    onEditEntity={!readOnly && post.objectType === 'EVENT' ? handleStartEditingScheduledEntity : undefined}
                    readOnly={readOnly}
                    socialFooter={buildSocialFooter(post)}
                  />
                </div>
              );
            }

            const normalizedBody = normalizePostBody(post.body);
            const rendered = renderPostBodyWithLinks(normalizedBody);
            const primaryLink = extractPrimaryLinkFromText(normalizedBody);
            const shouldRenderFeaturedLink = Boolean(primaryLink) && !isSupportedLinkPreviewUrl(primaryLink);
            const mediaUrl = findFirstImageAttachmentUrl(post, { publicMedia });
            const isPinned = isPinnedPost(post);
            const isFeature = isPinned || idx === 0;
            const typeLabel = isPinned ? 'Pinned' : (mediaUrl ? 'Photo' : null);
            const canManage = !readOnly && canViewerManagePost(post);
            const {
              displayName,
              avatarUrl,
              enableProfilePreview,
              showSuggestedBadge,
              showOwnerBadge,
              relatedBand,
            } = resolvePostCardIdentity(post, currentActionUserId, { readOnly });
            const showContextBadge = showSuggestedBadge || showOwnerBadge;
            const contextBadgeKey = showOwnerBadge
              ? 'home.postCard.badges.owner'
              : 'home.postCard.badges.suggestedForYou';
            const displayAvatarUrl = publicMedia
              ? ((relatedBand ? toPublicBandImageUrl(avatarUrl) : toPublicProfilePictureUrl(avatarUrl)) || avatarUrl)
              : avatarUrl;
            const formattedTimestamp = formatDateTime(post?.createdAt, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            });
            const compactTimestamp = (() => {
              if (!post?.createdAt) return '';
              const datePart = formatDate(post.createdAt, { year: undefined, month: 'short' });
              const timePart = formatTime(post.createdAt);
              if (datePart && timePart) return `${datePart} · ${timePart}`;
              return datePart || timePart || '';
            })();
            const relativeTimestamp = post?.createdAt ? formatLastSeenRelative(post.createdAt) : '';

            const actions = [];
            if (canManage && startEditingPost) {
              actions.push({
                id: 'edit',
                label: editLabel,
                onSelect: () => {
                  setDropdownOpenId(null);
                  startEditingPost(post);
                },
              });
            }
            if (canManage && handleRequestDeletePost) {
              actions.push({
                id: 'delete',
                label: deleteLabel,
                onSelect: () => {
                  setDropdownOpenId(null);
                  handleRequestDeletePost(post);
                },
              });
            }

            const kebab = actions.length > 0 ? (
              <CardActionsMenu
                isOpen={dropdownOpenId === post.id}
                onToggle={toggleDropdown}
                itemId={post.id}
                actions={actions}
              />
            ) : null;

            const bodyNode = normalizedBody ? (
              <ContentWithLinkPreviews previewUrls={rendered.previews}>
                <div className="home-posts__body" style={{ whiteSpace: 'pre-wrap' }}>
                  {rendered.content}
                </div>
              </ContentWithLinkPreviews>
            ) : null;

            return (
              <EditorialPostCard
                key={post.id}
                variant={mediaUrl ? 'media' : 'text'}
                feature={isFeature}
                typeLabel={typeLabel}
                mediaUrl={mediaUrl}
                topRight={kebab}
                meta={(
                  <div className="editorial-post-header">
                    <UserCardAvatar
                      userId={relatedBand ? undefined : (post.userId || undefined)}
                      username={displayName}
                      avatarUrl={relatedBand ? undefined : (displayAvatarUrl || undefined)}
                      resolvedAvatar={relatedBand ? {
                        avatarUrl: displayAvatarUrl || undefined,
                        shouldUseSupportAvatar: false,
                      } : undefined}
                      fallbackSrc={relatedBand ? bandIcon : undefined}
                      radius={relatedBand ? '14px' : undefined}
                      alt={`${displayName || (relatedBand ? 'Band' : 'User')} ${relatedBand ? 'image' : 'avatar'}`}
                      publicMedia={publicMedia}
                      className={classNames('editorial-post-header__avatar', {
                        'editorial-post-header__avatar--band': Boolean(relatedBand),
                      })}
                      enableProfilePreview={enableProfilePreview}
                    />
                    <div className="editorial-post-header__identity">
                      <div className="editorial-post-header__identity-row">
                        <span className="editorial-post-header__name">{displayName}</span>
                        {showContextBadge ? (
                          <span className="editorial-post-header__badge editorial-post-header__badge--spotlight">
                            <SpotlightIcon />
                            {t(contextBadgeKey)}
                          </span>
                        ) : null}
                        {typeLabel && !mediaUrl ? (
                          <span className="editorial-post-header__type-tag">{typeLabel}</span>
                        ) : null}
                      </div>
                      <div className="editorial-post-header__date-row">
                        {compactTimestamp ? (
                          <time
                            className="editorial-post-header__timestamp"
                            dateTime={post.createdAt || undefined}
                            title={formattedTimestamp || undefined}
                          >
                            {compactTimestamp}
                          </time>
                        ) : null}
                        {compactTimestamp && relativeTimestamp ? (
                          <span className="editorial-post-header__meta-dot" aria-hidden="true" />
                        ) : null}
                        {relativeTimestamp ? (
                          <span className="editorial-post-header__timestamp editorial-post-header__timestamp--relative">
                            {relativeTimestamp}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )}
                title={bodyNode}
                footer={buildSocialFooter(post)}
              >
                {shouldRenderFeaturedLink ? (
                  <PostLinkPreview
                    href={primaryLink}
                    className="home-posts__link-preview"
                  />
                ) : null}
              </EditorialPostCard>
            );
          })}
        </EditorialCardRail>
      ) : (
        <EditorialEmptyState
          title={emptyStateTitle}
          description={emptyStateDescription}
          action={emptyStateAction}
        />
      )}

      {timelineItems.length > 0 && hasMorePosts && typeof loadMorePosts === 'function' ? (
        <div className="home-posts__load-more">
          <button
            type="button"
            className="home-posts__load-more-btn"
            onClick={loadMorePosts}
            disabled={isLoadingMorePosts}
          >
            {isLoadingMorePosts ? loadingMoreLabel : loadMoreLabel}
          </button>
        </div>
      ) : null}

      <ConfirmDialog
        isOpen={deleteDialogState.isOpen}
        title={t('home.postCard.deleteDialog.title')}
        message={deleteDialogMessage}
        confirmLabel={t('home.postCard.deleteDialog.confirm')}
        cancelLabel={t('common.cancel')}
        processingLabel={t('home.postCard.deleteDialog.processing')}
        onConfirm={handleConfirmDeletePost}
        onCancel={handleCancelDeletePost}
        isProcessing={deleteDialogState.isProcessing}
        error={deleteDialogState.error || undefined}
        confirmVariant="danger"
      />

      {openPostId ? (() => {
        const openPost = postItems.find((p) => p.id === openPostId);
        if (!openPost) return null;
        return (
          <PostDetailModal
            post={openPost}
            currentUserId={currentActionUserId}
            readOnly={readOnly}
            onClose={() => setOpenPostId(null)}
            dropdownOpenId={dropdownOpenId}
            onToggleDropdown={readOnly ? null : toggleDropdown}
            onDelete={readOnly ? null : handleRequestDeletePost}
            onPostMetaChange={readOnly ? null : onPostMetaChange}
            onEditPost={(post) => {
              if (readOnly) return;
              setOpenPostId(null);
              startEditingPost?.(post);
            }}
          />
        );
      })() : null}

      {openScheduledEntityCommentsId ? (() => {
        const openItem = timelineItems.find((item) => item.id === openScheduledEntityCommentsId);
        if (!openItem) return null;
        return (
          <ScheduledEntityCommentsModal
            item={openItem}
            currentUserId={currentActionUserId}
            readOnly={readOnly}
            onClose={() => setOpenScheduledEntityCommentsId(null)}
            onItemMetaChange={readOnly ? null : onPostMetaChange}
          />
        );
      })() : null}

      {!readOnly && editingScheduledEntity ? (
        <EventCreateModal
          isOpen
          mode="edit"
          event={editingScheduledEntity}
          onClose={() => setEditingScheduledEntity(null)}
          onUpdated={(updated) => {
            if (updated?.id) {
              onPostMetaChange?.(updated.id, updated);
            }
            setEditingScheduledEntity(null);
          }}
          onDeleted={(deleted) => {
            if (deleted?.id) {
              onDeleteTimelineItem?.(deleted.id);
            }
            setEditingScheduledEntity(null);
          }}
        />
      ) : null}
    </HomeSection>
  );
}

HomePostsSection.propTypes = {
  id: PropTypes.string,
  eyebrow: PropTypes.node.isRequired,
  title: PropTypes.node.isRequired,
  side: PropTypes.node,
  showSide: PropTypes.bool,
  posts: PropTypes.array,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  readOnly: PropTypes.bool,
  allowComposer: PropTypes.bool,
  isComposerOpen: PropTypes.bool,
  composerMode: PropTypes.string,
  openCreateComposer: PropTypes.func,
  startEditingPost: PropTypes.func,
  closeComposer: PropTypes.func,
  editingPost: PropTypes.object,
  postContent: PropTypes.string,
  setPostContent: PropTypes.func,
  postVisibility: PropTypes.string,
  setPostVisibility: PropTypes.func,
  editContent: PropTypes.string,
  setEditContent: PropTypes.func,
  editVisibility: PropTypes.string,
  setEditVisibility: PropTypes.func,
  postAttachments: PropTypes.array,
  editAttachments: PropTypes.array,
  addCreateAttachments: PropTypes.func,
  removeCreateAttachment: PropTypes.func,
  addEditAttachments: PropTypes.func,
  removeEditAttachment: PropTypes.func,
  postAttachmentError: PropTypes.any,
  editAttachmentError: PropTypes.any,
  isSubmitting: PropTypes.bool,
  maxAttachments: PropTypes.number,
  postVisibilityOptions: PropTypes.array,
  createPost: PropTypes.func,
  handleUpdatePost: PropTypes.func,
  deletePost: PropTypes.func,
  onPostMetaChange: PropTypes.func,
  onDeleteTimelineItem: PropTypes.func,
  hasMorePosts: PropTypes.bool,
  isLoadingPosts: PropTypes.bool,
  isLoadingMorePosts: PropTypes.bool,
  loadMorePosts: PropTypes.func,
  showComposerToolbar: PropTypes.bool,
  renderComposeButton: PropTypes.func,
  editLabel: PropTypes.string,
  deleteLabel: PropTypes.string,
  emptyTitle: PropTypes.node,
  emptyDescription: PropTypes.node,
  loadingTitle: PropTypes.node,
  loadingDescription: PropTypes.node,
  postsError: PropTypes.node,
  postsErrorTitle: PropTypes.node,
  postsErrorDescription: PropTypes.node,
  retryPostsLabel: PropTypes.node,
  onRetryPosts: PropTypes.func,
  composeLabel: PropTypes.node,
  loadMoreLabel: PropTypes.node,
  loadingMoreLabel: PropTypes.node,
  reactionsLabel: PropTypes.string,
  commentsLabel: PropTypes.string,
  publicMedia: PropTypes.bool,
};
